import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
	buildOptionsList,
	resolveSelectedDisplay,
} from "./iconSelectUtils";
import "./IconSelect.scss";

function IconSelect({
	id,
	value,
	onChange,
	disabled = false,
	className = "",
	icon,
	placeholderOption,
	options = [],
}) {
	const [open, setOpen] = useState(false);
	const rootRef = useRef(null);
	const normalizedValue = value == null || value === "" ? "" : String(value);

	const allOptions = useMemo(
		() => buildOptionsList(options, placeholderOption),
		[options, placeholderOption]
	);

	const { label: displayLabel, icon: displayIcon } = resolveSelectedDisplay({
		value,
		options,
		placeholderOption,
		icon,
	});

	useEffect(() => {
		if (!open) return undefined;

		const handlePointerDown = (event) => {
			if (!rootRef.current?.contains(event.target)) {
				setOpen(false);
			}
		};

		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				setOpen(false);
			}
		};

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [open]);

	const handleToggle = () => {
		if (disabled) return;
		setOpen((current) => !current);
	};

	const handleSelect = (option) => {
		if (disabled) return;
		setOpen(false);
		onChange?.({
			target: {
				id,
				value: option.value,
			},
		});
	};

	return (
		<div
			ref={rootRef}
			className={[
				"icon-select",
				className,
				open ? "is-open" : "",
				disabled ? "is-disabled" : "",
			]
				.filter(Boolean)
				.join(" ")}
		>
			<button
				type="button"
				id={id}
				className="icon-select-trigger"
				disabled={disabled}
				aria-haspopup="listbox"
				aria-expanded={open}
				onClick={handleToggle}
			>
				{displayIcon && (
					<span className="icon-select-icon" aria-hidden="true">
						{displayIcon}
					</span>
				)}
				<span className="icon-select-value">{displayLabel}</span>
				<span className="icon-select-caret" aria-hidden="true">▾</span>
			</button>

			{open && (
				<ul className="icon-select-menu" role="listbox" aria-labelledby={id}>
					{allOptions.map((option) => {
						const optionIcon = option.icon ?? icon;
						const isSelected = option.value === normalizedValue;

						return (
							<li
								key={option.value}
								role="option"
								aria-selected={isSelected}
								className={`icon-select-option${isSelected ? " is-selected" : ""}`}
								onClick={() => handleSelect(option)}
							>
								{optionIcon && (
									<span className="icon-select-option-icon" aria-hidden="true">
										{optionIcon}
									</span>
								)}
								<span className="icon-select-option-label">{option.label}</span>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
}

IconSelect.propTypes = {
	id: PropTypes.string,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onChange: PropTypes.func,
	disabled: PropTypes.bool,
	className: PropTypes.string,
	icon: PropTypes.node,
	placeholderOption: PropTypes.shape({
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		label: PropTypes.string.isRequired,
		icon: PropTypes.node,
	}),
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			label: PropTypes.string.isRequired,
			icon: PropTypes.node,
		})
	),
};

export default IconSelect;
